import { useState } from "react";
import { db } from "../firebase/config";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { LEAD_TYPES, submitLeadEmail } from "../services/leadEmail";
import { Button } from "./ui/button";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "./ui/field";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const TRANSIT_DEALERSHIP_OPTIONS = [
  { label: "София Люлин", email: "liulin2@motopfohe.bg" },
  { label: "София Младост", email: "mladost@motopfohe.bg" },
  { label: "Благоевград", email: "blagoevgrad@motopfohe.bg" },
  { label: "Бургас", email: "burgas@motopfohe.bg" },
  { label: "Варна", email: "varna@motopfohe.bg" },
  { label: "Велико Търново", email: "velikotarnovo@motopfohe.bg" },
  { label: "Враца", email: "vratsa@motopfohe.bg" },
  { label: "Габрово", email: "gabrovo@motopfohe.bg" },
  { label: "Добрич", email: "dobrich@motopfohe.bg" },
  { label: "Казанлък", email: "kazanlak@motopfohe.bg" },
  { label: "Плевен", email: "pleven@motopfohe.bg" },
  { label: "Пловдив", email: "plovdiv@motopfohe.bg" },
  { label: "Русе", email: "ruse@motopfohe.bg" },
  { label: "Сливен", email: "sliven@motopfohe.bg" },
  { label: "Стара Загора", email: "stara_zagora@motopfohe.bg" },
  { label: "София Център", email: "sofia@motopfohe.bg" },
  { label: "Шумен", email: "shumen@motopfohe.bg" },
];

const transitContactSchema = z.object({
  name: z.string().trim().min(1, "Моля, въведете Вашето име"),
  phone: z
    .string()
    .trim()
    .min(1, "Моля, въведете телефонен номер")
    .regex(
      /^(?:\+359|0)\s?\d{1,2}\s?\d{3}\s?\d{3,4}$/,
      "Моля, въведете валиден телефонен номер",
    ),
  email: z
    .string()
    .trim()
    .min(1, "Моля, въведете имейл-адрес")
    .email("Моля, въведете валиден имейл адрес"),
  dealership: z
    .string()
    .trim()
    .min(1, "Моля, изберете предпочитано представителство"),
});

function TransitContactForm({ formType, onClose }) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(transitContactSchema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      dealership: "",
    },
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await addDoc(collection(db, "sixty-years-transit"), {
        ...data,
        type: formType,
        createdAt: serverTimestamp(),
      });

      const selectedDealership = TRANSIT_DEALERSHIP_OPTIONS.find(
        (dealership) => dealership.email === data.dealership,
      );

      await submitLeadEmail({
        type:
          formType === "testdrive"
            ? LEAD_TYPES.CAMPAIGN_TESTDRIVE
            : LEAD_TYPES.CAMPAIGN_OFFER,
        customer: {
          name: data.name,
          email: data.email,
          phone: data.phone,
        },
        dealership: {
          email: data.dealership,
          label: selectedDealership?.label ?? "",
        },
        vehicle: {
          model: "Transit 60 години",
        },
        campaign: {
          slug: "sixty-years-transit",
        },
        source: {
          pageUrl: window.location.href,
        },
        message:
          formType === "testdrive"
            ? "Заявка за тестдрайв (Transit 60 години)"
            : "Заявка за оферта (Transit 60 години)",
        title:
          formType === "testdrive"
            ? "Заявка за тестдрайв - Transit 60 години"
            : "Заявка за оферта - Transit 60 години",
      });

      setSuccess(true);
      reset();
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  if (success)
    return (
      <div className="p-6 text-center">
        <p className="text-xl text-fordBlue font-fordMedium">
          Благодарим! Ще се свържем с вас скоро.
        </p>
        <button
          onClick={onClose}
          className="px-6 py-2 mt-6 text-white rounded-full bg-fordBlue"
        >
          Затвори
        </button>
      </div>
    );

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="mx-auto w-full max-w-2xl p-6 text-left"
      noValidate
    >
      <h3 className="mb-4 text-2xl font-fordBold text-fordBlue">
        {formType === "testdrive" ? "Заявка за тестдрайв" : "Заявка за оферта"}
      </h3>

      <FieldGroup className="gap-5">
        <Field data-invalid={!!errors.name}>
          <FieldLabel htmlFor="transit-name">Име</FieldLabel>
          <Input
            id="transit-name"
            aria-invalid={!!errors.name}
            className="h-11 rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
            {...register("name")}
          />
          <FieldError>{errors.name?.message}</FieldError>
        </Field>

        <Field data-invalid={!!errors.phone}>
          <FieldLabel htmlFor="transit-phone">Телефон</FieldLabel>
          <Input
            id="transit-phone"
            type="tel"
            aria-invalid={!!errors.phone}
            className="h-11 rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
            {...register("phone")}
          />
          <FieldError>{errors.phone?.message}</FieldError>
        </Field>

        <Field data-invalid={!!errors.email}>
          <FieldLabel htmlFor="transit-email">Имейл</FieldLabel>
          <Input
            id="transit-email"
            type="email"
            aria-invalid={!!errors.email}
            className="h-11 rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
            {...register("email")}
          />
          <FieldError>{errors.email?.message}</FieldError>
        </Field>

        <Field data-invalid={!!errors.dealership}>
          <FieldLabel>Предпочитано представителство</FieldLabel>
          <Controller
            name="dealership"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger
                  className="h-12 w-full rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
                  aria-invalid={!!errors.dealership}
                >
                  <SelectValue placeholder="Изберете представителство" />
                </SelectTrigger>
                <SelectContent className="bg-white font-fordAntennaRegular">
                  {TRANSIT_DEALERSHIP_OPTIONS.map((dealership) => (
                    <SelectItem
                      key={dealership.email}
                      value={dealership.email}
                    >
                      {dealership.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          <FieldError>{errors.dealership?.message}</FieldError>
        </Field>
      </FieldGroup>

      <Button
        type="submit"
        disabled={loading}
        size="lg"
        className="mt-6 w-full rounded-full bg-fordBlue text-white hover:bg-fordLight"
      >
        {loading ? "Изпращане..." : "Изпрати"}
      </Button>
    </form>
  );
}

export default TransitContactForm;
